import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { useData } from '../context/DataContext';
import { api } from '../lib/api';
import { CheckCircle2, Users } from 'lucide-react';

const PostRequirementForm = ({ open, onOpenChange, initialValues = null }) => {
  const { toast } = useToast();
  const { categories } = useData();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    product: '', category: '', quantity: '', unit: 'Pieces', name: '', mobile: '', city: '', detail: '',
  });

  // When the dialog opens with pre-filled values (e.g. the buyer typed into
  // the compact homepage hero card first), copy them into local state so the
  // user isn't forced to retype what they just filled in.
  React.useEffect(() => {
    if (open && initialValues) {
      setForm((prev) => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(initialValues).filter(([, v]) => v !== undefined && v !== null && v !== ''),
        ),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product || !form.mobile || !form.name) {
      toast({ title: 'Fill product, name and mobile', variant: 'destructive' });
      return;
    }
    try {
      setSubmitting(true);
      await api.createRequirement(form);
      toast({ title: 'Requirement Posted!', description: 'You will receive quotes from verified sellers shortly.' });
      onOpenChange(false);
      setForm({ product: '', category: '', quantity: '', unit: 'Pieces', name: '', mobile: '', city: '', detail: '' });
    } catch (err) {
      toast({ title: 'Failed to post requirement', description: err?.response?.data?.detail || 'Try again later.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#2c54b2] text-xl">Tell us what you need, and we&apos;ll help you get quotes</DialogTitle>
          <DialogDescription className="flex items-center gap-4 text-xs pt-1">
            <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> Verified Sellers</span>
            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-green-600" /> 2+ Lakh Vendors</span>
            <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> 100% Free</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3 pt-2">
          <div className="col-span-2">
            <Label className="text-xs">What are you looking for? *</Label>
            <Input value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="e.g., MS Steel Pipes" />
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent className="max-h-60">
                {(categories || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Quantity</Label>
              <Input value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="100" />
            </div>
            <div>
              <Label className="text-xs">Unit</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pieces">Pieces</SelectItem>
                  <SelectItem value="Kilogram">Kilogram</SelectItem>
                  <SelectItem value="Meter">Meter</SelectItem>
                  <SelectItem value="Litre">Litre</SelectItem>
                  <SelectItem value="Box">Box</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Your Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
          </div>
          <div>
            <Label className="text-xs">Mobile *</Label>
            <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+91 " />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">City</Label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="e.g., Hyderabad" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Additional Details</Label>
            <Textarea value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} placeholder="Specifications, expected delivery, target price..." rows={3} />
          </div>
          <div className="col-span-2">
            <Button type="submit" disabled={submitting} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11 text-base">{submitting ? 'Submitting...' : 'Submit Requirement'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PostRequirementForm;
